/**
 * Typed wrapper for the agent's `/api/history/*` maintenance surface (F1)
 * plus a thin re-export of the existing readings query (`/api/timeseries`).
 *
 * The history *viewer* needs three things from the agent:
 *   1. Readings for a (deviceId, sensorId) window — served by the existing
 *      `/api/timeseries` route (we delegate to `fetchTimeSeries`).
 *   2. Storage statistics — `GET /api/history/stats`.
 *   3. Retention-policy read/write — `GET|PUT /api/history/retention`.
 *   4. Manual prune — `POST /api/history/prune`.
 *
 * All maintenance routes are owned by `HistoryRouter` on the agent. Shapes
 * mirror `TimeSeriesStats` / `RetentionPolicy` from `@reefapp/shared`.
 */
import type { RetentionPolicy, TimeSeriesStats } from '@reefapp/shared';

import { agentFetch } from './agent-fetch';
import { loadPublicEnv } from './env';
import {
  fetchTimeSeries,
  type Result,
  type TimeSeriesPoint,
  type TimeSeriesQuery,
} from './timeseries-api';

export type { Result, TimeSeriesPoint, TimeSeriesQuery };

function baseUrl(): string {
  return loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
}

function errorFromBody(body: unknown, status: number, statusText: string): string {
  if (
    body &&
    typeof body === 'object' &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  ) {
    return (body as { error: string }).error;
  }
  return `HTTP ${status} ${statusText}`;
}

/** Narrow an unknown blob to a `TimeSeriesStats`. */
function asStats(v: unknown): TimeSeriesStats | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (
    typeof o.rowCount === 'number' &&
    (o.oldestTs === null || typeof o.oldestTs === 'number') &&
    (o.newestTs === null || typeof o.newestTs === 'number') &&
    typeof o.approxBytes === 'number' &&
    typeof o.seriesCount === 'number'
  ) {
    return {
      rowCount: o.rowCount,
      oldestTs: o.oldestTs as number | null,
      newestTs: o.newestTs as number | null,
      approxBytes: o.approxBytes,
      seriesCount: o.seriesCount,
    };
  }
  return null;
}

/** Narrow an unknown blob to a `RetentionPolicy`. */
function asPolicy(v: unknown): RetentionPolicy | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.mode === 'off') return { mode: 'off' };
  if (o.mode === 'keepDays' && typeof o.keepDays === 'number') {
    return { mode: 'keepDays', keepDays: o.keepDays };
  }
  return null;
}

export const historyApi = {
  /** Reuse the existing `/api/timeseries` query for the chart body. */
  query(q: TimeSeriesQuery): Promise<Result<TimeSeriesPoint[]>> {
    return fetchTimeSeries(q);
  },

  /** GET /api/history/stats → storage statistics for the gauge. */
  async getStats(): Promise<Result<TimeSeriesStats>> {
    try {
      const res = await agentFetch(`${baseUrl()}/api/history/stats`, {
        method: 'GET',
        headers: { 'content-type': 'application/json' },
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore parse failure
      }
      if (!res.ok) {
        return { ok: false, error: errorFromBody(body, res.status, res.statusText) };
      }
      const stats =
        body && typeof body === 'object' && 'stats' in body
          ? asStats((body as { stats: unknown }).stats)
          : null;
      if (!stats) return { ok: false, error: 'malformed stats response' };
      return { ok: true, value: stats };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  },

  /** POST /api/history/prune { beforeTs } → number of deleted rows. */
  async prune(beforeTs: number): Promise<Result<{ deleted: number }>> {
    try {
      const res = await agentFetch(`${baseUrl()}/api/history/prune`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ beforeTs }),
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore parse failure
      }
      if (!res.ok) {
        return { ok: false, error: errorFromBody(body, res.status, res.statusText) };
      }
      const deleted =
        body &&
        typeof body === 'object' &&
        'deleted' in body &&
        typeof (body as { deleted: unknown }).deleted === 'number'
          ? (body as { deleted: number }).deleted
          : 0;
      return { ok: true, value: { deleted } };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  },

  /** GET /api/history/retention → current retention policy. */
  async getRetention(): Promise<Result<RetentionPolicy>> {
    try {
      const res = await agentFetch(`${baseUrl()}/api/history/retention`, {
        method: 'GET',
        headers: { 'content-type': 'application/json' },
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore parse failure
      }
      if (!res.ok) {
        return { ok: false, error: errorFromBody(body, res.status, res.statusText) };
      }
      const policy =
        body && typeof body === 'object' && 'policy' in body
          ? asPolicy((body as { policy: unknown }).policy)
          : null;
      if (!policy) return { ok: false, error: 'malformed retention response' };
      return { ok: true, value: policy };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  },

  /** PUT /api/history/retention (body = policy) → normalized policy. */
  async setRetention(policy: RetentionPolicy): Promise<Result<RetentionPolicy>> {
    try {
      const res = await agentFetch(`${baseUrl()}/api/history/retention`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(policy),
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore parse failure
      }
      if (!res.ok) {
        return { ok: false, error: errorFromBody(body, res.status, res.statusText) };
      }
      const next =
        body && typeof body === 'object' && 'policy' in body
          ? asPolicy((body as { policy: unknown }).policy)
          : null;
      if (!next) return { ok: false, error: 'malformed retention response' };
      return { ok: true, value: next };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  },
};
